// FEAT-025 SPEC-RG-04 — External Dealer 화이트리스트 가드 (테스트 친화 분리).
// AuthRoute / AdminAuthRoute 가 import 하여 사용. 단위 테스트는 본 파일을 직접 import.

import { ROLE_EXTERNAL_DEALER, ORG_HQ, USER_TYPE_BRANCH } from '../config/appConstants';
import { ROUTES } from '../config/routes';

// External Dealer 정책 — BUG-101: admin 화이트리스트(현재 ROUTES.ADMIN_LICENSE,
// ROUTES.ADMIN_LOGIN, '/Admin/AdminNotification', ROUTES.ADMIN_MY_INFO, ROUTES.ADMIN_UPDATE_PASSWORD 5경로, 아래 EXTERNAL_DEALER_ADMIN_ALLOWED_PREFIXES
// 참조) 외 모든 경로를 차단. tech 포털(/Home 등) 포함해 비허용 경로는 /Admin/License 로 회수.
// 이전 정책(비-admin 경로 전체 허용)은 "외부 딜러는 라이선스만 접근" 요건과 불일치하여 폐기.
//
// 주의 — FE 가드는 UX 보호. 실제 인가는 BE authcheck SSoT (FEAT-025b).
// IMP-138: 화이트리스트는 normalizePathname() 의 lowercase 출력과 비교하므로
// ROUTES 정본에서 .toLowerCase() 로 파생한다(대소문자 통일).
export const EXTERNAL_DEALER_ADMIN_ALLOWED_PREFIXES = [
    ROUTES.ADMIN_LICENSE,
    ROUTES.ADMIN_LOGIN,
    // F-017: 2026-08-19 VOC 확정 — 외부 딜러도 자기 알림을 받는다. ROUTES 에 항목이 없어(범위 밖)
    // AdminTopMenu.js 의 isNotificationPage 가 비교하는 경로 문자열을 그대로 가져왔다. 단, 매칭 규약은
    // 서로 다르다 — 이쪽(EXTERNAL_DEALER_ADMIN_ALLOWED_PREFIXES)은 normalizePathname() 으로
    // 소문자 정규화한 뒤 세그먼트 경계(`/`,`?`)까지 포함해 비교하고, AdminTopMenu.js 의
    // isNotificationPage 는 `location.pathname === '/Admin/AdminNotification'` 대소문자 구분
    // 완전일치다. 한쪽만 고친다고 다른 쪽이 갱신되지 않으니 경로 문자열 변경 시 양쪽 모두 확인한다.
    '/Admin/AdminNotification',
    // IMP-478 P7a: 서버는 본인 정보 수정(update)·비밀번호 변경(updatePassword)을 외부 딜러 토큰에도 이미 허용한다
    // (2026-10-14 코드 분석). 접두 일치라 /admin/myinfo/setting 등 하위 경로가 함께 열리는 것은 의도.
    ROUTES.ADMIN_MY_INFO,
    ROUTES.ADMIN_UPDATE_PASSWORD,
].map((p) => p.toLowerCase());

// 미허용 경로 진입 시 회수 목적지.
export const EXTERNAL_DEALER_LANDING = ROUTES.ADMIN_LICENSE;

// PR #192 review (Security Major) — pathname 정규화. 이중 슬래시(`//admin/...`),
// 백슬래시(`\admin\...`), URL 인코딩(`%2f`) 등으로 admin 화이트리스트 검사 우회 방지.
//   - 백슬래시(`\`) → 슬래시(`/`) 치환 (Windows 경로 패턴 우회 방어)
//   - 연속 슬래시 단일화 (`//admin` → `/admin`)
//   - URL 인코딩 디코드 (`%2f` → `/`) — 클라 측에서 인코딩 살아 있는 케이스 방어
//   - 끝나는 슬래시 정리
export const normalizePathname = (pathname) => {
    if (!pathname || typeof pathname !== 'string') return '';
    let p = pathname;
    try {
        // %2f 등 URL 인코딩이 살아 있을 가능성 — decode 실패해도 원본 유지.
        p = decodeURIComponent(p);
    } catch {
        /* malformed URI 는 원본 그대로 검사 */
    }
    // 백슬래시 → 슬래시
    p = p.replace(/\\/g, '/');
    // 연속 슬래시 단일화
    p = p.replace(/\/+/g, '/');
    return p.toLowerCase();
};

// BUG-101: 비-admin 경로 전체 허용 예외 제거 — 화이트리스트만 허용.
export const isExternalDealerPathAllowed = (pathname) => {
    if (!pathname || typeof pathname !== 'string') return false;
    const lower = normalizePathname(pathname);
    return EXTERNAL_DEALER_ADMIN_ALLOWED_PREFIXES.some(
        (p) => lower === p || lower.startsWith(`${p}/`) || lower.startsWith(`${p}?`),
    );
};

// PR #192 review (Architecture Major A-3) — admin 경로 진입 가드 SSoT.
// `app.js useEffect` 는 hard-load 시 1회만 평가되므로 SPA 내비게이션(history.push)
// 으로 admin 경로 진입은 본 함수가 SSoT 로 막는다.
//
// 보안 결정 분기 — 순수 함수로 분리하여 단위 테스트 가능.
//   role: getLicenseRole(user) 결과 (HQ/Subsidiary/InternalDealer/ExternalDealer/null)
//   permAdminAccess: user.permAdminAccess 불리언
//   pathname: 진입 시도 경로
// 반환 — { allow: boolean, redirectTo?: string }
export const evaluateRouteAccess = ({ role, permAdminAccess, pathname }) => {
    if (!pathname || typeof pathname !== 'string') return { allow: false, redirectTo: ROUTES.HOME };
    const lower = normalizePathname(pathname);
    const isAdminPath = lower.startsWith('/admin/') || lower === '/admin';

    // External Dealer 는 EXTERNAL_DEALER_ADMIN_ALLOWED_PREFIXES 화이트리스트 외 admin 경로 차단.
    // 일반 사용자 경로(/Home, /Sites 등) 는 모두 허용.
    if (role === ROLE_EXTERNAL_DEALER) {
        if (isExternalDealerPathAllowed(pathname)) return { allow: true };
        return { allow: false, redirectTo: EXTERNAL_DEALER_LANDING };
    }

    // permAdminAccess 가 false 이면 admin 경로 차단. ExternalDealer 는 위에서 처리됨.
    if (isAdminPath && !permAdminAccess) {
        return { allow: false, redirectTo: ROUTES.HOME };
    }

    return { allow: true };
};

// SEC-041 — RayData(Warranty 정책) 메뉴 노출 게이트.
// 대상: HQ/Branch Super Admin(adminType 'S') 또는 Global su.
// Dealer 및 일반 사용자(adminType != 'S')는 미노출. 메뉴는 1차 UX 제어이며
// 최종 조회/쓰기 권한은 BE API 에서 검증한다(SEC-039 연계, 스펙 §3.2.2·§5).
// allowlist(fail-closed): HQ/BRANCH 만 허용(Subsidiary 는 UserType 상 BRANCH 로 흡수).
// 미지/undefined userType 은 자동 차단.
//   userType: UserType(companyId, org) 결과 ('HQ'/'BRANCH'/'DEALER')
//   adminType: user.adminType ('S' = Super Admin)
//   su: Global su (state.AuthReducer.su)
export const canSeeRayDataMenu = ({ userType, adminType, su } = {}) =>
    !!su || ((userType === ORG_HQ || userType === USER_TYPE_BRANCH) && adminType === 'S');

// C-4 (IMP-353): warranty 실행 계열 권한 가드 SSoT. 현행 산발 인라인 가드를 §5 매트릭스
// 기준 named 함수로 통일(동작 보존 추출). §5는 Global su=O 도 명시하나 현행 FE 는 su bypass
// 미적용 → 동작 보존(업무확인 시 추가). BE 가 최종 SSoT(FE 는 UX 가드).

// Apply Warranty Standard 실행 게이트. 공통 전제 + category 별 org 분기.
//   공통(IMP-362 #239): adminType==='S' || isSetWarranty===true — 원 SEC-040 은 AND 였으나,
//     isSetWarranty 부여 UI 부재로 봉인되던 버튼을 Super Admin 에도 노출하기 위해 완화(담당자 승인).
//   HQ(제조): 공통 + org==='HQ'.
//   BRANCH(자사): 공통 + userType==='BRANCH' (FEAT-042 #223 Branch 버튼 배선).
export const canApplyWarrantyStandard = ({ adminType, isSetWarranty, org, userType, category } = {}) => {
    if (adminType !== 'S' && isSetWarranty !== true) return false;
    if (category === 'HQ') return org === ORG_HQ;
    if (category === 'BRANCH') return userType === USER_TYPE_BRANCH;
    return false;
};

export const canEditManufacturerWarranty = ({ userType, adminType } = {}) => userType === ORG_HQ && adminType === 'S';

export const canEditBranchWarranty = ({ userType, adminType } = {}) => userType === USER_TYPE_BRANCH && adminType === 'S';

// BUG-222: 삭제 대상 문서의 category 를 병합행에서 역판정한다. RayDataAction.js GetAll 은
// (companyId, warrantyType, models) 이 같은 HQ 문서와 BRANCH 문서를 한 행으로 병합하는데,
// 병합행의 `data.category` 는 먼저 들어온 문서 값으로 고정되어 신뢰할 수 없다. 그래서 실제
// 삭제 타겟 _id 를 hqId/branchId 와 직접 비교해 역판정하고, 매칭 실패 시 null 을 반환해
// canDeleteRayDataPolicy 가 fail-closed 로 차단하게 한다.
export const resolveRayDataDeleteCategory = ({ _id, hqId, branchId } = {}) => {
    if (!_id) return null;
    if (hqId && _id === hqId) return 'HQ';
    if (branchId && _id === branchId) return 'BRANCH';
    return null;
};

// category 는 위 resolveRayDataDeleteCategory 의 역판정 결과만 받는다. BE 는 삭제 시 category
// 를 검증하지 않으며(정책 변동 여지를 남긴 의도된 설계) FE 가 UX 가드로 조직 매칭을 담당한다.
// 미지/null/빈 category 는 fail-closed 로 차단한다.
export const canDeleteRayDataPolicy = ({ userType, adminType, category } = {}) => {
    if (category === 'HQ') return userType === ORG_HQ;
    if (category === 'BRANCH') return userType === USER_TYPE_BRANCH && adminType === 'S';
    return false;
};

// SEC-051: operator(제품 소유 회사 companyId) 일괄 이전 권한.
//   BE 는 update / updatebulk / updatetransferbulk 3경로 모두 canCallerChangeCompanyIdTo 로
//   `adminType === 'S'` 를 선행 하드 게이트한 뒤 target ∈ caller active subtree 를 검사한다.
//   FE 는 그 앞단(adminType)만 판정해 헛수고를 막고, subtree 검증은 BE 를 SSoT 로 둔다.
//
//   su(=companyInfo.org === 'HQ')를 쓰지 않는 이유 — su 는 adminType 을 보지 않으므로 HQ 소속
//   Manager/User 도 su=true 이고, 그 기준으로 열면 BE 가 403 으로 막는 계정에 버튼이 노출된다.
//
//   BUG-193 이 AdminProductStockList 에 인라인 상수로 도입한 판정을, SEC-051 에서 같은 정책이
//   필요한 화면이 3곳(BE type 기준으로는 `updatetransferbulk` 2곳 —
//   AdminProductList / AdminProductCompanyImport — 과 `updatebulk` 1곳 —
//   AdminProductStockList)임을 확인하면서 공유 primitive 로 승격했다. 세 화면 모두 BE 가
//   canCallerChangeCompanyIdTo 로 같은 선행 게이트를 걸므로 판정도 한 곳이어야 한다.
//
//   호출부는 형제 게이트들과 동일하게 claims bag 을 구성해 넘긴다 — raw redux `user` 를 그대로
//   넘기면 미로그인·초기화 중 명시적 null 에서 구조분해가 throw 한다.
export const canTransferProductOperator = ({ adminType } = {}) => adminType === 'S';
